<template>
  <Card :title="$t('global.password-protection')">
    <p>{{ $t("global.password-protection-description") }}</p>
    <LoadingState v-if="isLoading()" :size="50" />
    <ErrorState v-else-if="isErrored()" />
    <CardActionContent v-else :buttons="getButtons()">
      <template v-if="isEnabled()" #description>
        <CardListContent :list="getList()" />
      </template>
    </CardActionContent>
  </Card>
</template>

<script>
import { computed, onMounted } from "@vue/composition-api";
import Card from "@/components/display/Card";
import CardActionContent from "@/components/display/card-sub-components/CardActionContent";
import CardListContent from "@/components/display/card-sub-components/CardListContent";
import DisablePasswordProtection from "@/components/smart/modals/DisablePasswordProtection";
import EnablePasswordProtection from "@/components/smart/modals/EnablePasswordProtection";
import ErrorState from "@/components/display/ErrorState";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import ResetHttpPassword from "@/components/smart/modals/ResetHttpPassword";
import store from "@/store";
import ViewPassword from "@/components/smart/modals/ViewPassword";

export default {
  name: "PasswordProtection",
  components: {
    Card,
    CardActionContent,
    CardListContent,
    ErrorState,
    LoadingState,
  },
  props: {
    resourceName: String,
    site: Object,
  },
  setup(props, { root }) {
    const isProtectionSubmitting = computed(() =>
      store.getters["passwordProtection/isUpdating"](props.site.id)
    );
    const user = computed(() => store.getters["auth/getUser"]);
    const password = computed(() => store.getters["httpPassword/get"]);
    const passwordStatus = computed(() => store.getters["httpPassword/getStatus"]);
    const protection = computed(() => store.getters["passwordProtection/get"]);
    const protectionStatus = computed(() => store.getters["passwordProtection/getStatus"]);

    const disableProtection = () =>
      store.dispatch("modals/open", {
        template: DisablePasswordProtection,
        data: { resourceName: props.resourceName, site: props.site },
      });

    const enableProtection = () =>
      store.dispatch("modals/open", {
        template: EnablePasswordProtection,
        data: { resourceName: props.resourceName, site: props.site },
      });

    const getButtons = () => {
      if (isEnabled()) {
        return [
          new NavLink(
            root.$t("global.reset-password"),
            null,
            resetPassword,
            false,
            "secondary",
            "epsilon",
            !protectionStatus.value.isLoaded || !passwordStatus.value.isLoaded
          ),
          new NavLink(
            root.$t("global.disable"),
            null,
            disableProtection,
            false,
            "primary",
            "epsilon",
            !protectionStatus.value.isLoaded
          ),
        ];
      }
      return [
        new NavLink(
          root.$t("global.enable"),
          null,
          enableProtection,
          false,
          "primary",
          "epsilon",
          !protectionStatus.value.isLoaded,
          !protectionStatus.value.isLoaded
        ),
      ];
    };

    const getList = () => {
      const pwdActions = getPassword()
        ? [
            new NavLink(
              root.$t("global.view-password"),
              "view",
              viewPassword,
              false,
              null,
              "epsilon",
              !passwordStatus.value.isLoaded
            ),
          ]
        : undefined;

      return [
        { label: root.$t("global.username"), content: user.value.email, canCopy: true },
        {
          label: root.$t("global.password"),
          content: root.$t("global.password-hidden"),
          actions: pwdActions,
        },
      ];
    };

    const getPassword = () => password.value;
    const isEnabled = () => protection.value?.enabled;
    const isErrored = () => protectionStatus.value.isErrored;
    const isLoading = () => protectionStatus.value.isLoading || isProtectionSubmitting.value;

    const resetPassword = () =>
      store.dispatch("modals/open", {
        template: ResetHttpPassword,
        data: { resourceName: props.resourceName, site: props.site },
      });

    const viewPassword = () =>
      store.dispatch("modals/open", {
        template: ViewPassword,
        data: { password: getPassword() },
      });

    onMounted(() => {
      store.dispatch("passwordProtection/get", props.site.id).then((protection) => {
        const uuid = protection?.dataShare?.uuid;
        if (protection.enabled && uuid) {
          store.dispatch("httpPassword/get", { id: props.site.id, uuid });
        }
      });
    });

    return {
      getButtons,
      getList,
      isEnabled,
      isErrored,
      isLoading,
    };
  },
};
</script>
